import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MoreHorizontal, Search, UserCog, Eye } from 'lucide-react';
import { toast } from 'sonner';
import AdminTable from '@/components/AdminTable';

const ROLE_BADGES = {
  admin:     { label: 'Admin',     style: { backgroundColor: '#EDE9FE', color: '#5B21B6' } },
  member:    { label: 'Member',    style: { backgroundColor: '#D1FAE5', color: '#065F46' } },
  applicant: { label: 'Applicant', style: { backgroundColor: '#FEF3C7', color: '#92400E' } },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Members() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [roleDialog, setRoleDialog] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback((role) => {
    setLoading(true);
    const params = role === 'all' ? '' : `?role=${role}`;
    api.get(`/admin/members${params}`)
      .then(r => setUsers(r.data))
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(roleFilter); }, [roleFilter, fetchUsers]);

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openRoleDialog = (user) => {
    setRoleDialog(user);
    setNewRole(user.role);
  };

  const submitRoleChange = async () => {
    if (!roleDialog) return;
    setSubmitting(true);
    try {
      await api.put(`/admin/members/${roleDialog.id}/role`, { role: newRole });
      toast.success('Role updated successfully');
      setRoleDialog(null);
      fetchUsers(roleFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell title="Members">
      <div className="space-y-5">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <Tabs value={roleFilter} onValueChange={setRoleFilter}>
            <TabsList style={{ backgroundColor: 'var(--bg-subtle)' }}>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="member">Members</TabsTrigger>
              <TabsTrigger value="applicant">Applicants</TabsTrigger>
              <TabsTrigger value="admin">Admins</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="pl-8 text-sm"
              style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
            />
          </div>
        </div>

        {/* Table */}
        <AdminTable
          data={filtered}
          loading={loading}
          dataTestId="admin-members-table"
          emptyTitle="No users found"
          emptySubtitle="Try adjusting your filters or search."
          columns={[
            {
              key: 'user', label: 'User',
              render: (u) => (
                <button onClick={() => navigate(`/admin/members/${u.id}`)} className="flex items-center gap-3 text-left transition-opacity hover:opacity-75" data-testid={`member-row-${u.id}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: 'var(--gold)' }}>
                    {u.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text)' }}>{u.name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{u.email}</p>
                  </div>
                </button>
              ),
            },
            { key: 'joined', label: 'Joined', render: (u) => <span style={{ color: 'var(--text-muted)' }}>{formatDate(u.created_at)}</span> },
            {
              key: 'role', label: 'Role',
              render: (u) => {
                const badge = ROLE_BADGES[u.role] || ROLE_BADGES.applicant;
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium" style={badge.style}>{badge.label}</span>;
              },
            },
            { key: 'status', label: 'Status', render: (u) => <span className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{u.membership_status || '—'}</span> },
            {
              key: 'actions', label: '', width: '60px',
              render: (u) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
                    <DropdownMenuItem onClick={() => navigate(`/admin/members/${u.id}`)} className="gap-2 cursor-pointer">
                      <Eye size={14} /> View profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openRoleDialog(u)} className="gap-2 cursor-pointer">
                      <UserCog size={14} /> Change role
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </div>

      {/* Role Change Dialog */}
      <Dialog open={!!roleDialog} onOpenChange={() => setRoleDialog(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Change role — {roleDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>New role</label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
                <SelectItem value="applicant">Applicant</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRoleDialog(null)} disabled={submitting} style={{ borderRadius: '10px' }}>Cancel</Button>
            <Button
              onClick={submitRoleChange}
              disabled={submitting || newRole === roleDialog?.role}
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}
            >
              {submitting ? 'Saving…' : 'Save role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
